import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  CheckCircle2,
  Plane,
  Hotel,
  Ship,
  Car,
  Utensils,
  Mail,
  Phone
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

// Voyager Pro / Figma reskin - 'editorial' (Editorial / Luxury Magazine) is
// a cream/near-black palette with a rust/amber accent in Figma - close in
// spirit to the previous white/gray-900 look, recolored to the exact hex
// values and given a real accent color for price/CTA emphasis.
const PALETTE = TEMPLATE_PALETTES.editorial_magazine;

const EditorialMagazineTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} ${quote.client.lastName}`;
    }
    return 'Distinguished Guest';
  };

  return (
    <div className="min-h-screen" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      {/* Hero Section - Massive, Edge-to-Edge */}
      <div className="relative min-h-[50vh] flex items-center justify-center" style={{ backgroundColor: 'var(--primary)' }}>
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl md:text-7xl mb-6 leading-tight tracking-tight" style={{ color: 'var(--background)' }}>
            {quote.quoteTitle || 'Your Bespoke Journey'}
          </h1>
          {quote.travelDates?.start && (
            <p className="text-lg md:text-xl font-light tracking-widest uppercase" style={{ color: 'var(--background)', opacity: 0.8 }}>
              {formatDate(quote.travelDates.start)}
              {quote.travelDates.end && ` — ${formatDate(quote.travelDates.end)}`}
            </p>
          )}
        </div>
      </div>

      {/* Main Content - Magazine Layout with Massive Whitespace */}
      <div className="max-w-3xl mx-auto px-6 md:px-12">

        {/* Introduction Section */}
        <div className="py-20">
          <p className="text-sm uppercase tracking-widest mb-4" style={{ color: 'var(--muted-foreground)' }}>Curated For</p>
          <h2 className="font-serif text-4xl mb-8" style={{ color: 'var(--foreground)' }}>
            {getClientName()}
          </h2>

          {(quote.travelers?.adults || quote.travelers?.children?.length) && (
            <div className="flex items-center gap-2 text-sm tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
              <Users className="w-4 h-4" />
              <span>
                {quote.travelers.adults || 0} Guest{quote.travelers.adults !== 1 ? 's' : ''}
                {quote.travelers.children?.length > 0 && ` · ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}`}
              </span>
            </div>
          )}
        </div>

        {/* Itinerary - Editorial Style with Thin Dividers */}
        {quote.components && quote.components.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)' }}>
            {quote.components.map((component, index) => (
              <div key={component.id || index} className="py-16" style={{ borderBottom: '1px solid var(--border)' }}>
                {/* Component Number */}
                <div className="text-xs uppercase tracking-widest mb-6" style={{ color: 'var(--muted-foreground)' }}>
                  Chapter {index + 1}
                </div>

                {/* Component Title - Large Serif */}
                <h3 className="font-serif text-3xl md:text-4xl mb-6 leading-tight" style={{ color: 'var(--foreground)' }}>
                  {component.title || `Experience ${index + 1}`}
                </h3>

                {/* Component Type Badge */}
                <div className="mb-8">
                  <span className="text-xs uppercase tracking-widest pb-1" style={{ color: 'var(--accent)', borderBottom: '1px solid var(--accent)' }}>
                    {component.type || 'Exclusive Experience'}
                  </span>
                </div>

                {/* Component Details - Clean, Spacious */}
                {component.data && Object.keys(component.data).length > 0 && (
                  <div className="space-y-6 leading-relaxed tracking-wide" style={{ color: 'var(--foreground)' }}>
                    {Object.entries(component.data).map(([key, value]) => {
                      if (!value || key === 'isOption' || key === 'optionDescription') return null;

                      const isPriceField = key.toLowerCase().includes('price') ||
                                           key.toLowerCase().includes('cost') ||
                                           key.toLowerCase().includes('fee') ||
                                           key.toLowerCase().includes('total') ||
                                           key.toLowerCase().includes('deposit');
                      const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);

                      let displayValue = String(value);
                      if (isPriceField && isNumeric) {
                        displayValue = formatCurrency(parseFloat(value));
                      }

                      return (
                        <div key={key} className="flex flex-col md:flex-row md:items-baseline gap-2">
                          <dt className="text-xs uppercase tracking-widest md:w-48 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }}>
                            {humanizeKey(key)}
                          </dt>
                          <dd className="text-base font-light" style={{ color: 'var(--foreground)' }}>
                            {displayValue}
                          </dd>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Component Photos */}
                {component.photos && component.photos.length > 0 && (
                  <div className="mt-8 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
                    <h4 className="text-xs uppercase tracking-widest mb-4 flex items-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
                      <span>📸</span> Gallery
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {component.photos.map((photo, photoIndex) => (
                        <div
                          key={photoIndex}
                          className="relative group cursor-pointer overflow-hidden"
                          style={{ backgroundColor: 'var(--background)' }}
                          onClick={() => window.open(photo.url, '_blank')}
                        >
                          <img
                            src={photo.url}
                            alt={photo.name || 'Component photo'}
                            className="w-full h-auto object-contain"
                            style={{ maxHeight: '300px' }}
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white font-medium">{photo.name || 'View Photo'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Component Documents */}
                {component.documents && component.documents.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-xs uppercase tracking-widest mb-3 flex items-center gap-2" style={{ color: 'var(--muted-foreground)' }}>
                      <span>📋</span> Documents
                    </h4>
                    <div className="space-y-2">
                      {component.documents.map((doc, docIndex) => (
                        <a
                          key={docIndex}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 transition-colors text-sm hover:opacity-70"
                          style={{ color: 'var(--muted-foreground)' }}
                        >
                          <span>📄</span>
                          <span>{doc.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {hasMultipleOptions(component) && (
                  <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                    <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {getComponentOptions(component).map((option, optIndex) => (
                        <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                          {option.photoUrl && (
                            <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                          )}
                          <div className="p-4">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                              {option.badge && (
                                <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                              )}
                            </div>
                            {option.description && (
                              <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                            )}
                            <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Component Price - Elegant Display */}
                <div className="mt-12 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Investment</span>
                    <span className="font-serif text-2xl" style={{ color: 'var(--accent)' }}>
                      {formatCurrency(component.price)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Notes Section - Editorial Style */}
        {quote.notes && (
          <div className="py-20" style={{ borderTop: '1px solid var(--border)' }}>
            <p className="text-xs uppercase tracking-widest mb-6" style={{ color: 'var(--muted-foreground)' }}>Essential Details</p>
            <div className="prose prose-lg max-w-none">
              <p className="leading-relaxed tracking-wide font-light whitespace-pre-wrap" style={{ color: 'var(--foreground)' }}>
                {quote.notes}
              </p>
            </div>
          </div>
        )}

        {/* Investment Summary - Minimal, Elegant */}
        <div className="py-20" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="space-y-12">
            {/* Total Investment */}
            <div>
              <p className="text-xs uppercase tracking-widest mb-4" style={{ color: 'var(--muted-foreground)' }}>Total Investment</p>
              <p className="font-serif text-5xl md:text-6xl" style={{ color: 'var(--foreground)' }}>
                {formatCurrency(quote.totalPrice)}
              </p>
            </div>

            {/* Deposit Information */}
            {quote.depositAmount > 0 && (
              <div className="pt-8" style={{ borderTop: '1px solid var(--border)' }}>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Initial Deposit</p>
                    <p className="font-serif text-3xl" style={{ color: 'var(--accent)' }}>
                      {formatCurrency(quote.depositAmount)}
                    </p>
                  </div>

                  {quote.depositDueDate && (
                    <div>
                      <p className="text-xs uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Due Date</p>
                      <p className="text-xl font-light" style={{ color: 'var(--foreground)' }}>
                        {formatDate(quote.depositDueDate)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Button - Minimal Design */}
            {onPayment && (
              <div className="pt-12">
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full font-light tracking-widest uppercase text-sm py-6 px-8 transition-all duration-300 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-110'
                  }`}
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--background)' }}
                >
                  {isAgentPreview ? 'Preview Mode' : 'Reserve Your Journey'}
                </button>
                {!isAgentPreview && (
                  <p className="text-center text-xs mt-6 tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                    Secure payment · Complimentary concierge service included
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-16 pt-12" style={{ borderTop: '1px solid var(--border)' }}>
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-2xl font-light mb-2 tracking-wide" style={{ color: 'var(--foreground)' }}>
                {quote.agent_name || 'Your Travel Curator'}
              </h3>
              {quote.agent_business_name && (
                <p className="text-sm uppercase tracking-widest mb-6" style={{ color: 'var(--muted-foreground)' }}>{quote.agent_business_name}</p>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
                {quote.agent_email && (
                  <a
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-2 transition-colors hover:opacity-70"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{quote.agent_email}</span>
                  </a>
                )}

                {quote.agent_email && quote.agent_phone && (
                  <span className="hidden sm:block" style={{ color: 'var(--border)' }}>|</span>
                )}

                {quote.agent_phone && (
                  <a
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-2 transition-colors hover:opacity-70"
                    style={{ color: 'var(--muted-foreground)' }}
                  >
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer - Minimal Branding */}
        <div className="py-16 text-center" style={{ borderTop: '1px solid var(--border)' }}>
          <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
            Bespoke Travel Experiences
          </p>
        </div>
      </div>
    </div>
  );
};

export default EditorialMagazineTemplate;
